import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { api, ApiError } from '../lib/api'
import { DraftProvider, useDraftContext } from '../state/DraftProvider'
import { usePlayerNews } from '../state/usePlayerNews'
import DraftHeader from '../components/draft/DraftHeader'
import PlayerPool from '../components/draft/PlayerPool'
import PickBar from '../components/draft/PickBar'
import BoardPanel from '../components/draft/BoardPanel'
import RecentPicks from '../components/draft/RecentPicks'
import ScoutPanel from '../components/draft/ScoutPanel'
import QueuePanel from '../components/QueuePanel'
import RosterPanel from '../components/RosterPanel'
import type { QueueRow } from '../components/QueuePanel'
import type { DraftResponse } from '../types/protocol'

// The owner's draft room. One sticky header, one primary surface (the pool),
// and everything else behind tabs on a phone / in side columns on a laptop.
//
// The panel-visibility rule below is deliberately CSS-only: every panel mounts
// once and stays mounted, and the breakpoint decides whether the tab state is
// consulted at all. A JS media query would remount panels on resize and throw
// away the pool's scroll position and filter state.

type Tab = 'players' | 'queue' | 'roster' | 'board'

export default function Draft() {
  const { code = '' } = useParams()
  return (
    <DraftProvider code={code}>
      <DraftRoom code={code.toUpperCase()} />
    </DraftProvider>
  )
}

function DraftRoom({ code }: { code: string }) {
  const {
    draft,
    players,
    loading,
    error,
    teamToken,
    queue,
    queueBusy,
    queueError,
    addToQueue,
    removeFromQueue,
    reorderQueue,
  } = useDraftContext()

  // Hoisted above the early returns below — a hook called after a conditional
  // return breaks the rules of hooks. The commissioner token is derived from
  // it further down.
  const { hash } = useLocation()

  const [tab, setTab] = useState<Tab>('players')
  // Decision: the lg column shows the dossier by default — a scouting panel you
  // have to ask for is the drawer again. Nothing auto-switches this.
  const [col, setCol] = useState<'scout' | 'list'>('scout')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pickError, setPickError] = useState<string | null>(null)

  const playersById = useMemo(() => new Map(players.map((p) => [p.id, p])), [players])
  const draftedIds = useMemo(
    () => new Set(draft?.picks.filter((p) => p.player_id !== null).map((p) => p.player_id) ?? []),
    [draft?.picks],
  )
  const queueRows: QueueRow[] = useMemo(
    () =>
      queue.map((playerId) => ({
        playerId,
        player: playersById.get(playerId),
        taken: draftedIds.has(playerId),
      })),
    [queue, playersById, draftedIds],
  )

  // Self-healing: if someone drafts the player you had selected, the selection
  // simply stops resolving rather than leaving a stale name in the pick bar.
  // Derived up here, above the early returns, because usePlayerNews below is a
  // hook and must run on every render — `loading` starts true, so the first
  // paint always takes the return below.
  const selectedPlayer =
    selectedId != null && !draftedIds.has(selectedId)
      ? (playersById.get(selectedId) ?? null)
      : null

  // Called exactly once for the whole room and passed down as props: both
  // dossier containers mount at all times, so a hook inside the shared body
  // would fire two identical requests per selection. Selection going null the
  // instant someone drafts that player also cancels an in-flight request.
  const news = usePlayerNews(code, selectedPlayer?.id ?? null)

  if (loading) {
    return (
      <main className="min-h-screen px-4 py-16 text-center text-sm text-ink-lo">
        Loading the draft room…
      </main>
    )
  }

  if (error || !draft) {
    return (
      <main className="min-h-screen px-4 py-16 text-center">
        <p className="text-crit">{error ?? 'No draft data'}</p>
        <Link
          to="/"
          className="mt-6 inline-block text-sm text-ink-lo underline-offset-2 hover:text-ink-hi hover:underline"
        >
          Back to start
        </Link>
      </main>
    )
  }

  // #ct=<token>, not ?ct=<token>: the fragment never reaches the server, so
  // there is no access log at any layer holding the credential. Reading the
  // hash the router gave us also means a navigation updates this.
  const commissionerToken = new URLSearchParams(hash.slice(1)).get('ct')
  const isCommissioner = !!commissionerToken

  const myTeam = draft.teams.find((team) => team.id === draft.my_team_id)
  const currentPick = draft.picks.find((p) => p.overall === draft.current_pick_overall)
  const isMyPick = draft.my_team_id != null && currentPick?.team_id === draft.my_team_id
  const inProgress = draft.status === 'live' || draft.status === 'paused'

  const canPick = inProgress && (isCommissioner || isMyPick)
  const canQueue = teamToken != null && draft.my_team_id != null

  async function makePick() {
    if (selectedPlayer == null || !canPick) return
    setSubmitting(true)
    setPickError(null)
    try {
      await api<DraftResponse>(`/drafts/${code}/picks`, {
        method: 'POST',
        body: { player_id: selectedPlayer.id },
        commissionerToken: commissionerToken ?? undefined,
        teamToken: teamToken ?? undefined,
      })
      setSelectedId(null)
    } catch (e) {
      setPickError(e instanceof ApiError ? e.message : 'Pick failed')
    } finally {
      setSubmitting(false)
    }
  }

  // The third tab is always present because it also carries Recent picks —
  // labelling it "Queue" only when there is a queue keeps a spectator on a
  // phone from losing the pick feed entirely.
  const tabs: Array<{ id: Tab; label: string; badge?: string }> = [
    { id: 'players', label: 'Players' },
    canQueue
      ? { id: 'queue', label: 'Queue', badge: String(queue.length) }
      : { id: 'queue', label: 'Picks' },
  ]
  if (draft.my_team_id != null) tabs.push({ id: 'roster', label: 'Roster' })
  if (draft.picks.length > 0) tabs.push({ id: 'board', label: 'Board' })

  // Active tab shows on a phone; every panel shows at lg regardless.
  const panel = (id: Tab) => (tab === id ? 'block' : 'hidden lg:block')

  return (
    <main className="flex min-h-screen flex-col">
      <div className="sticky top-0 z-30">
        <DraftHeader draft={draft} myTeam={myTeam} teamToken={teamToken} />

        {tabs.length > 1 && (
          <nav
            aria-label="Draft room sections"
            className="flex gap-1 border-b border-line bg-surface-1/95 px-2 shadow-raised backdrop-blur-sm lg:hidden"
          >
            {tabs.map((entry) => (
              <button
                key={entry.id}
                type="button"
                aria-current={tab === entry.id ? 'page' : undefined}
                onClick={() => setTab(entry.id)}
                className={`flex items-center gap-1.5 border-b-2 px-2.5 py-2.5 text-xs font-semibold whitespace-nowrap ${
                  tab === entry.id
                    ? 'border-signal text-ink-hi'
                    : 'border-transparent text-ink-lo hover:text-ink-mid'
                }`}
              >
                {entry.label}
                {entry.badge && (
                  <span
                    className={`rounded-full px-1.5 font-mono text-[10px] ${
                      tab === entry.id ? 'bg-signal/20 text-signal' : 'bg-surface-3 text-ink-lo'
                    }`}
                  >
                    {entry.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        )}
      </div>

      {draft.status === 'setup' && (
        <Banner>This draft hasn&apos;t started yet. The pool below is what you&apos;ll be picking from.</Banner>
      )}
      {draft.status === 'complete' && (
        <Banner tone="signal">
          Draft complete.{' '}
          <Link to={`/results/${draft.join_code}`} className="underline underline-offset-2">
            See the full results →
          </Link>
        </Banner>
      )}
      {isCommissioner && inProgress && (
        <CommissionerStrip code={code} commissionerToken={commissionerToken} joinCode={draft.join_code} />
      )}

      <div className="mx-auto w-full max-w-7xl flex-1 px-3 pb-8 sm:px-5">
        {/* The template has to track which panels actually render: a spectator
            has no roster column, and with a fixed three-column template grid
            auto-placement would drop the pool into the 16rem gutter and hand
            the flexible middle to Recent picks. The pool is the primary
            surface — it takes minmax(0,1fr) in both shapes. The board is
            col-span-full so it spans whichever count is in play. */}
        <div
          className={`lg:grid lg:items-start lg:gap-5 lg:pt-5 ${
            draft.my_team_id != null
              ? 'lg:grid-cols-[16rem_minmax(0,1fr)_18rem]'
              : 'lg:grid-cols-[minmax(0,1fr)_18rem]'
          }`}
        >
          {draft.my_team_id != null && (
            <div className={panel('roster')}>
              <RosterPanel draft={draft} teamId={draft.my_team_id} playersById={playersById} />
            </div>
          )}

          <div className={panel('players')}>
            <PlayerPool
              players={players}
              draftedIds={draftedIds}
              selectedId={selectedPlayer?.id ?? null}
              onSelect={setSelectedId}
              queue={queue}
              queueBusy={queueBusy}
              canQueue={canQueue}
              onQueue={(id) => void addToQueue(id)}
              onUnqueue={(id) => void removeFromQueue(id)}
              hasCustomRankings={draft.rankings_count > 0}
            />
          </div>

          <div className={`${panel('queue')} space-y-4 pt-4 lg:pt-0`}>
            {/* A second CSS-only visibility layer inside the one grid child — the
                column cannot gain a sibling without changing the child count both
                lg:grid-cols templates above depend on. Below lg this column is
                only ever the queue and the pick feed; down there the dossier is
                the pick bar's drawer, so the toggle never shows and the
                queue/picks stack ignores `col` entirely. Getting that backwards
                hides Recent picks from a spectator's phone. */}
            <div className="hidden gap-1 border-b border-line lg:flex">
              {([['scout', 'Scout'], ['list', canQueue ? 'Queue' : 'Picks']] as const).map(
                ([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCol(id)}
                    aria-current={col === id ? 'page' : undefined}
                    className={`border-b-2 px-2.5 py-2 text-xs font-semibold whitespace-nowrap ${
                      col === id
                        ? 'border-signal text-ink-hi'
                        : 'border-transparent text-ink-lo hover:text-ink-mid'
                    }`}
                  >
                    {label}
                  </button>
                ),
              )}
            </div>

            <div className={col === 'scout' ? 'hidden lg:block' : 'hidden'}>
              <ScoutPanel
                player={selectedPlayer}
                news={news.items}
                newsLoading={news.loading}
                newsUnavailable={news.unavailable}
              />
            </div>

            <div className={`space-y-4 ${col === 'list' ? 'block' : 'block lg:hidden'}`}>
              {canQueue && (
                <QueuePanel
                  rows={queueRows}
                  busy={queueBusy}
                  error={queueError}
                  onReorder={(ids) => void reorderQueue(ids)}
                  onRemove={(id) => void removeFromQueue(id)}
                />
              )}
              <RecentPicks draft={draft} playersById={playersById} />
            </div>
          </div>

          {draft.picks.length > 0 && (
            <div className={`${panel('board')} pt-4 lg:col-span-full lg:pt-5`}>
              <BoardPanel draft={draft} players={players} />
            </div>
          )}
        </div>
      </div>

      <PickBar
        player={selectedPlayer}
        canPick={canPick}
        canQueue={canQueue}
        queued={selectedPlayer != null && queue.includes(selectedPlayer.id)}
        submitting={submitting}
        queueBusy={queueBusy}
        error={pickError}
        autopickOn={myTeam?.autopick_enabled ?? false}
        news={news.items}
        newsLoading={news.loading}
        newsUnavailable={news.unavailable}
        onPick={() => void makePick()}
        onQueue={() => selectedPlayer && void addToQueue(selectedPlayer.id)}
        onUnqueue={() => selectedPlayer && void removeFromQueue(selectedPlayer.id)}
        onDismiss={() => setSelectedId(null)}
      />
    </main>
  )
}

function Banner({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'signal'
}) {
  return (
    <div
      className={`border-b px-4 py-2.5 text-center text-sm ${
        tone === 'signal'
          ? 'border-signal/30 bg-signal/10 text-signal'
          : 'border-line bg-surface-1 text-ink-lo'
      }`}
    >
      {children}
    </div>
  )
}

// The full control set lives on the commissioner's own page. These two are
// here because they are the ones you reach for mid-room, with the board in
// front of you — everything else is a detour worth taking deliberately.
function CommissionerStrip({
  code,
  commissionerToken,
  joinCode,
}: {
  code: string
  commissionerToken: string | null
  joinCode: string
}) {
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  async function doAction(path: string) {
    setBusy(true)
    setActionError(null)
    try {
      await api<DraftResponse>(`/drafts/${code}/${path}`, {
        method: 'POST',
        commissionerToken: commissionerToken ?? undefined,
      })
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-1 px-3 py-2 sm:px-5">
      <span className="eyebrow">Commissioner</span>
      <button
        type="button"
        disabled={busy}
        onClick={() => void doAction('undo')}
        className="rounded-sm border border-line-strong bg-surface-3 px-2.5 py-1 text-xs font-semibold text-ink-mid shadow-raised hover:text-ink-hi disabled:opacity-40"
      >
        Undo last pick
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void doAction('skip')}
        className="rounded-sm border border-line-strong bg-surface-3 px-2.5 py-1 text-xs font-semibold text-ink-mid shadow-raised hover:text-ink-hi disabled:opacity-40"
      >
        Skip pick
      </button>
      <Link
        to={`/control#${commissionerToken}`}
        className="text-xs text-ink-lo underline-offset-2 hover:text-ink-hi hover:underline"
      >
        All controls →
      </Link>
      <Link
        to={`/board/${joinCode}`}
        className="text-xs text-ink-lo underline-offset-2 hover:text-ink-hi hover:underline"
      >
        Open the board →
      </Link>
      {actionError && <span className="text-xs text-crit">{actionError}</span>}
    </div>
  )
}
